import urls from '@/constants/endpoints/gateway';
import {
    AppointmentRequestDetailModel,
    AppointmentVisitTypesDTO,
    IdResponseDTO,
    InHomeAppointmentReferralRequestDTO,
    LastAppointmentRequestDTO,
    MciOrderDetailsResponseDTO,
    ReferralDateRequestDTO,
    ReferralDatesRequestDTO,
    SendAppointmentRequestDTO,
    SendToMciRequestData,
    SimpleMessageResponseDTO,
    SPIRequestMetaDTO,
    SPIStatusResponseDTO,
} from '@/types/gatewayDataModels';

import httpApi from './httpApi_new';

type DisableErrorCallback = (error?: unknown) => boolean;

export type ChangeVendorReferralDate = ({
    actorId,
    value,
}: {
    actorId: number;
    value: string;
}) => Promise<SimpleMessageResponseDTO>;

class SPIService {
    sendSPI = (data: SPIRequestMetaDTO): Promise<SPIStatusResponseDTO> =>
        httpApi.post({
            url: urls.SEND_SPI,
            data,
        });

    changeVendorReferralAcceptedDate: ChangeVendorReferralDate = ({actorId, value}) => {
        const data: ReferralDateRequestDTO = {dateTime: value};
        return httpApi.patch({
            url: urls.CHANGE_VENDOR_REFERRAL_ACCEPTED_DATE(actorId),
            data,
        });
    };

    changeVendorReferralSentDate: ChangeVendorReferralDate = ({actorId, value}) => {
        const data: ReferralDateRequestDTO = {dateTime: value};
        return httpApi.patch({
            url: urls.CHANGE_VENDOR_REFERRAL_SENT_DATE(actorId),
            data,
        });
    };

    changeVendorReferralDates = ({
        actorId,
        sentDateTime,
        acceptedDateTime,
    }: ReferralDatesRequestDTO & {actorId: number}): Promise<SimpleMessageResponseDTO> =>
        httpApi.patch({
            url: urls.CHANGE_VENDOR_REFERRAL_DATES(actorId),
            data: {sentDateTime, acceptedDateTime},
        });

    sendToServiceConnect = ({handOffVendorId}: {handOffVendorId: number}): Promise<SimpleMessageResponseDTO> =>
        httpApi.patch({
            url: urls.SEND_TO_SERVICE_CONNECT(handOffVendorId),
        });

    sendToMci = ({
        handOffVendorId,
        data,
        opts,
    }: {
        handOffVendorId: number;
        data?: SendToMciRequestData;
        opts?: {disableGeneralNotify: DisableErrorCallback};
    }): Promise<SimpleMessageResponseDTO> =>
        httpApi.patch({
            url: urls.SEND_TO_MCI(handOffVendorId),
            data,
            ...opts,
        });

    getMciOrderDetails = ({vendorHandOffId}: {vendorHandOffId: number}): Promise<MciOrderDetailsResponseDTO> =>
        httpApi.get({
            url: urls.GET_MCI_ORDER_DETAILS(vendorHandOffId),
        });

    cancelInServiceConnect = ({handOffVendorId}: {handOffVendorId: number}): Promise<SimpleMessageResponseDTO> =>
        httpApi.patch({
            url: urls.CANCEL_IN_SERVICE_CONNECT(handOffVendorId),
        });

    sendToLogisticEngine = ({handOffVendorId}: {handOffVendorId: number}): Promise<SimpleMessageResponseDTO> =>
        httpApi.patch({
            url: urls.SEND_TO_LOGISTICS_ENGINE(handOffVendorId),
        });

    changeAppointmentReferralDateTime = ({
        id,
        referrals,
    }: {
        id: number;
        referrals: InHomeAppointmentReferralRequestDTO;
    }): Promise<IdResponseDTO> => httpApi.put({url: urls.CHANGE_APPOINTMENT_REFERRAL_DATES(id), data: referrals});

    sendAppointmentRequest = (data: SendAppointmentRequestDTO) =>
        httpApi.post({url: urls.SEND_APPOINTMENT_REQUEST, data});

    getAppointmentVisitTypes = (): Promise<AppointmentVisitTypesDTO> =>
        httpApi.get({url: urls.APPOINTMENT_VISIT_TYPES});

    getGMRHistoryData = (id: number): Promise<LastAppointmentRequestDTO> =>
        httpApi.get({url: urls.LAST_APPOINTMENT_REQUEST(id)});

    getAppointmentRequestDetails = (id: number): Promise<AppointmentRequestDetailModel> =>
        httpApi.get({url: urls.APPOINTMENT_REQUEST_DETAILS(id)});
}

const spiService = new SPIService();
export default spiService;
